
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Star, Calendar, AlertCircle, Eye, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "./ui/button";
import { Decision, useDecisions } from "@/hooks/useDecisions";
import { useFormatDate } from "../src/hooks/useFormatDate";
import { classifyDecisionReviews } from "@/lib/review-classification";
import { clickableCardProps } from "@/lib/clickable-card";

interface RecentDecisionsProps {
  decisions: Decision[];
  onViewDecision: (id: string) => void;
  onViewAll: () => void;
}

export function RecentDecisions({ decisions, onViewDecision, onViewAll }: RecentDecisionsProps) {
  const { getCurrentRating } = useDecisions();
  const { formatDate } = useFormatDate();
  
  const getReviewStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-100 text-green-800";
      case "overdue": return "bg-red-100 text-red-800";
      case "upcoming": return "bg-gray-100 text-gray-600";
      case "due_now": return "bg-blue-100 text-blue-800";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getRatingTrend = (decision: Decision) => {
    const currentRating = getCurrentRating(decision);
    const initialRating = decision.day0_rating;
    const diff = currentRating - initialRating;
    
    if (diff > 0.5) return { icon: TrendingUp, color: "text-green-600", value: `+${diff.toFixed(1)}` };
    if (diff < -0.5) return { icon: TrendingDown, color: "text-red-600", value: diff.toFixed(1) };
    return { icon: Minus, color: "text-gray-500", value: "=" };
  };

  const getRatingHistoryPreview = (decision: Decision) => {
    if (decision.rating_history.length <= 1) return null;
    
    const ratings = decision.rating_history.slice(-4); // Show last 4 ratings
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <span>Trend:</span>
        {ratings.map((entry, index) => (
          <span key={index} className="px-1 py-0.5 bg-gray-100 rounded text-xs">
            {entry.rating}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Recent Decisions</h2>
        <Button variant="outline" size="sm" onClick={onViewAll}>
          View All
        </Button>
      </div>

      <div className="space-y-4">
        {decisions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground mb-4">No decisions recorded yet</p>
              <p className="text-sm text-muted-foreground">Start by creating your first decision!</p>
            </CardContent>
          </Card>
        ) : (
          decisions.map((decision) => {
            const currentRating = getCurrentRating(decision);
            const trend = getRatingTrend(decision);
            const TrendIcon = trend.icon;
            const historyPreview = getRatingHistoryPreview(decision);
            
            return (
              <Card
                key={decision.id}
                className="group cursor-pointer hover:shadow-md transition-shadow"
                {...clickableCardProps(() => onViewDecision(decision.id))}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                       <CardTitle className="text-lg group-hover:text-primary truncate">
                         {decision.title}
                       </CardTitle>
                       <CardDescription className="mt-1 line-clamp-2">
                         {decision.description}
                       </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <span className="text-sm font-medium">{currentRating}/10</span>
                      </div>
                      {decision.rating_history.length > 1 && (
                        <div className="flex items-center gap-1">
                          <TrendIcon className={`h-3 w-3 ${trend.color}`} />
                          <span className={`text-xs ${trend.color}`}>{trend.value}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(decision.decision_date)}
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {decision.tags?.filter(tag => tag?.name).map((tag) => (
                        <Badge key={tag.id} variant="secondary" className="text-xs">
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium">Rationale: </span>
                      <span className="text-sm text-muted-foreground">{decision.rationale}</span>
                    </div>
                    
                    {decision.rating_history.length > 1 && (
                      <div>
                        <span className="text-sm font-medium">Rating History: </span>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">
                            Initial: {decision.day0_rating}/10
                          </span>
                          <span className="text-xs text-muted-foreground">→</span>
                          <span className="text-xs text-muted-foreground">
                            Current: {currentRating}/10
                          </span>
                          {historyPreview}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">Reviews:</span>
                        {decision.scheduled_reviews.map((review, index) => (
                          <Badge 
                            key={index} 
                            variant="outline" 
                            className={`text-xs ${getReviewStatusColor(review.status)}`}
                          >
                            {review.day}d {review.status}
                          </Badge>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-2">
                          {(() => {
                            const { dueNowReviews, overdueReviews } = classifyDecisionReviews(decision);
                            return (dueNowReviews.length > 0 || overdueReviews.length > 0);
                          })() && (
                           <Badge variant="destructive" className="text-xs">
                             <AlertCircle className="h-3 w-3 mr-1" />
                             Review Due
                           </Badge>
                         )}
                        
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onViewDecision(decision.id)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
        
        {decisions.length > 0 && (
          <div className="flex justify-center pt-4">
            <Button 
              variant="ghost" 
              onClick={onViewAll}
              className="text-muted-foreground hover:text-primary"
            >
              See more decisions
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
